import { Filter, FilterValue } from 'support/types/filters';

import { isDefined } from '../generic/generic';

/**
 * Builds a URL query string based on filter parameters
 *
 * @param query - Optional filter object containing filter values and pagination parameters
 * @param query.filterValues - Array of filter key-value pairs to include in the URL
 * @param query.cursor - Pagination cursor to include in the URL
 * @param query.perPage - Number of items per page to include in the URL
 * @param query.include - Additional resources to include in the response
 * @param query.sort - Sort parameter for ordering results (e.g., 'name' for ascending, '-name' for descending)
 * @param options - Optional configuration
 * @param options.forApiRequest - If true, normalizes filter values for API compatibility
 *
 * @returns A formatted URL query string starting with '?' if parameters exist, or an empty string if no parameters are provided
 *
 * @example
 * // Returns '?filter[status]=active&perPage=10'
 * buildFilterUrl({
 *   filterValues: [{ key: 'status', value: 'active' }],
 *   perPage: 10
 * });
 *
 * @example
 * // Returns '?filter[action]=VAL1%2CVAL2' (normalized for API and URL-encoded)
 * buildFilterUrl({
 *   filterValues: [{ key: 'action', value: 'VAL1;VAL2' }]
 * }, { forApiRequest: true });
 *
 * @example
 * // Returns '?sort=-name' (descending order)
 * buildFilterUrl({ sort: '-name' });
 */
export const buildFilterUrl = (query?: Filter, options?: { forApiRequest?: boolean }): string => {
  if (!query) {
    return '';
  }

  const { filterValues, cursor, perPage, include, sort } = query;
  const encodeValue = (value: string | number) => encodeURIComponent(String(value));

  const urlParams =
    filterValues
      ?.filter((filter: FilterValue) => filter.value.length)
      .map((filter: FilterValue) => {
        const value = options?.forApiRequest ? filter.value.replace(/;/g, ',') : filter.value;
        return `filter[${filter.key}]=${encodeValue(value)}`;
      }) || [];

  if (cursor !== null && cursor !== undefined) {
    urlParams.push(`cursor=${encodeValue(cursor)}`);
  }

  if (include && include !== '') {
    urlParams.push(`include=${encodeValue(include)}`);
  }

  if (perPage !== null && perPage !== undefined) {
    urlParams.push(`perPage=${encodeValue(perPage)}`);
  }

  if (sort !== null && sort !== undefined) {
    urlParams.push(`sort=${encodeValue(sort)}`);
  }

  return urlParams.length ? `?${urlParams.join('&')}` : '';
};

export const withParameter = (parameters: Array<string>): string => {
  return parameters.length ? `?with[]=${parameters.join(',')}` : '';
};

export const addQueryParametersToUrl = (url: string, params: Record<string, string | undefined>): string => {
  const urlObj = new URL(url, 'https://localhost'); // this base part of the url is discarded

  Object.entries(params).forEach(([key, value]) => {
    if (isDefined(value) && value !== '') {
      urlObj.searchParams.set(key, value);
    }
  });

  return urlObj.pathname + urlObj.search;
};
